/* eslint-disable @typescript-eslint/dot-notation */
/* eslint-disable prefer-arrow/prefer-arrow-functions */
/* eslint-disable @typescript-eslint/naming-convention */
import { HttpHeaders, HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { Activity, ActivityComment, Stream } from '../shared/models';
import { StorageService } from './storage.service';
import { map, } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class DataService {
  stravaUrl = 'https://www.strava.com/api/v3/athlete/activities';
  needsUpdate = false;
  lastDate;
  selectedActivities = [];
  constructor(
    private http: HttpClient,
    private storageService: StorageService
  ) {}

  get athlete() {
    return JSON.parse(localStorage.getItem('athlete'));
  }

  set athlete(athlete) {
    localStorage.setItem('athlete', athlete);
  }

  getAllActivities(): any {
    this.needsUpdate = false;
    return this.storageService.getAllActivities();
  }

  // getLatestActivity(): any {
  //   return this.http.get<Activity[]>(this.stravaUrl, {
  //     headers: new HttpHeaders({authorization: 'Bearer '+ localStorage.getItem('access_token')})
  //    }).pipe(switchMap(response=> this.getActivity(response[0].id.toString())));
  // }

  getFirstTimeActivities(): Promise<Activity[]> {
    return this.http
      .get<Activity[]>(this.stravaUrl, {
        headers: new HttpHeaders({
          authorization: 'Bearer ' + localStorage.getItem('access_token'),
        }),
      })
      .toPromise();
  }

  getPastActivities(time: Date): Observable<Activity[]> {
    const url = this.stravaUrl + '?before=' + new Date(time).getTime() / 1000;
    return this.http.get<Activity[]>(url, {
      headers: new HttpHeaders({
        authorization: 'Bearer ' + localStorage.getItem('access_token'),
      }),
    });
  }

  checkForNewActivities() {
    return this.http.get<Activity[]>(this.stravaUrl, {
      headers: new HttpHeaders({
        authorization: 'Bearer ' + localStorage.getItem('access_token'),
      }),
    });
  }

  getOlderActivities(timestamp): any {
    let url = this.stravaUrl;
    url += '?before=' + timestamp;
    return this.http
      .get<Activity[]>(url, {
        headers: new HttpHeaders({
          authorization: 'Bearer ' + localStorage.getItem('access_token'),
        }),
      })
      .toPromise();
  }

  getStreams(
    id: string,
    keys = 'latlng,time,distance,velocuty_smooth,grade_smooth,heartrate,cadence,watts,altitude,moving'
  ): Observable<Stream[]> {
    const url =
      'https://www.strava.com/api/v3/activities/' +
      id +
      '/streams?keys=' +
      keys;
    return this.http
      .get<Stream[]>(url, {
        headers: new HttpHeaders({
          authorization: 'Bearer ' + localStorage.getItem('access_token'),
        }),
      })
      .pipe(
        map((results) => {
          const distance = results.find((item) => item.type === 'distance')
            ?.data as number[];
          const time = results?.find((item) => item.type === 'time')
            ?.data as number[];
          if (distance && time) {
            results.push({
              type: 'speed',
              data: this.calculateSpeed(time, distance, 6),
            });
          }
          return results;
        })
      );
  }

  getActivityData(id: string): Observable<Activity> {
    const url = 'https://www.strava.com/api/v3/activities/' + id;
    return this.http
      .get<Activity>(url, {
        headers: new HttpHeaders({
          authorization: 'Bearer ' + localStorage.getItem('access_token'),
        }),
      })
      .pipe(map((res) => this.returnActivityWithAchievements(res)));
  }

  getActivityDataWithStreams(id: string): Observable<Activity> {
    return combineLatest([
      this.getActivityData(id),
      this.getStreams(id, 'altitude'),
    ]).pipe(map(([res, res1]) => ({ ...res, streams: res1 })));
  }

  getZones(): Observable<Activity> {
    const url = 'https://www.strava.com/api/v3/athlete/zones';
    return this.http.get<Activity>(url, {
      headers: new HttpHeaders({
        authorization: 'Bearer ' + localStorage.getItem('access_token'),
      }),
    });
  }

  getAthlete(): Observable<any> {
    const url = 'https://www.strava.com/api/v3/athlete';
    return this.http.get<any>(url, {
      headers: new HttpHeaders({
        authorization: 'Bearer ' + localStorage.getItem('access_token'),
      }),
    });
  }

  downloadAthleteClubs(page = 1) {
    localStorage.setItem('clubs', JSON.stringify({}));
    const url =
      'https://www.strava.com/api/v3/athlete/clubs?per_page=30&page=' + page;
    this.http
      .get<any>(url, {
        headers: new HttpHeaders({
          authorization: 'Bearer ' + localStorage.getItem('access_token'),
        }),
      })
      .subscribe((res) =>
        res.forEach((element) => {
          this.downloadProfilePhoto(
            element.name + '_' + element.verified,
            element.profile
          );
        })
      );
  }

  getComments(id: number): Observable<ActivityComment[]> {
    const url = 'https://www.strava.com/api/v3/activities/' + id + '/comments';
    return this.http.get<ActivityComment[]>(url, {
      headers: new HttpHeaders({
        authorization: 'Bearer ' + localStorage.getItem('access_token'),
      }),
    });
  }

  getLocation(lon, lat): Observable<any> {
    const url =
      'https://nominatim.openstreetmap.org/reverse?format=json&lon=' +
      lon +
      '&lat=' +
      lat;
    return this.http.get<any>(url);
  }

  getAthleteStats() {
    const id = JSON.parse(localStorage.getItem('athlete')).id;
    const url = 'https://www.strava.com/api/v3/athletes/' + id + '/stats';
    this.http
      .get<any>(url, {
        headers: new HttpHeaders({
          authorization: 'Bearer ' + localStorage.getItem('access_token'),
        }),
      })
      .subscribe((res) => localStorage.setItem('stats', JSON.stringify(res)));
  }

  calculatePace(distance, time) {
    if (!distance || distance === 0) {
      return NaN;
    }
    const isImperial =
      localStorage.getItem('imperial') &&
      localStorage.getItem('imperial') === 'true';
    if (isImperial) {
      return Math.floor((time * 1609) / distance);
    }
    return Math.floor((time * 1000) / distance);
  }

  getSegmentDetails(id: number): Observable<any> {
    const url = 'https://www.strava.com/api/v3/segments/' + id;
    return this.http.get<any>(url);
  }

  private downloadProfilePhoto(name, url) {
    const reader = this.getFileReader();
    reader.onloadend = function() {
      if (reader.result) {
        const clubs = JSON.parse(localStorage.getItem('clubs')) ?? {};
        clubs[name] = reader.result
          .toString()
          .replace('application/octet-stream', 'image/jpeg');
        localStorage.setItem('clubs', JSON.stringify(clubs));
      }
    };
    fetch(url)
      .then((resp) => resp.blob())
      .then((blob) => {
        reader.readAsDataURL(blob);
      })
      .catch(() => this.downloadPrivateProfilePhoto(name, url));
  }

  private downloadPrivateProfilePhoto(name, url) {
    const proxy = 'https://wsrv.nl/?url=';
    const reader = this.getFileReader();
    reader.onloadend = function() {
      if (reader.result) {
        const clubs = JSON.parse(localStorage.getItem('clubs')) ?? {};
        const x = reader.result
          .toString()
          .replace('application/octet-stream', 'image/jpeg');
        clubs[name] = x;
        localStorage.setItem('clubs', JSON.stringify(clubs));
      }
    };
    fetch(proxy + url)
      .then((resp) => resp.blob())
      .then((blob) => {
        reader.readAsDataURL(blob);
      })
      .catch(() => console.log('No public photo'));
  }

  private getFileReader(): FileReader {
    const fileReader = new FileReader();
    const zoneOriginalInstance = (fileReader as any)[
      '__zone_symbol__originalInstance'
    ];
    return zoneOriginalInstance || fileReader;
  }

  private returnActivityWithAchievements(activity: Activity): Activity {
    const achievements = [];
    const segments = [];
    activity.segment_efforts?.forEach((effort) => {
      if (effort.achievements?.length > 0) {
        effort.achievements.forEach((element) => {
          segments.push({
            ...element,
            segment: effort.segment,
            name: effort.name,
            distance: effort.distance,
            moving_time: effort.moving_time,
            pr_rank: element.rank,
            average_heartrate: effort.average_heartrate,
            average_cadence: effort.average_cadence,
          });
        });
      }
    });
    activity.best_efforts?.forEach((best) => {
      if (best.achievements?.length > 0) {
        best.achievements?.forEach((element) => {
          achievements.push({
            ...element,
            name: best.name,
            distance: best.distance,
            moving_time: best.moving_time,
            pr_rank: best.pr_rank,
          });
        });
      }
    });
    activity.achievements =achievements;
    activity.segments = segments;
    return activity;
  }

  private calculateSpeed(
    elapsedSeconds: number[],
    distances: number[],
    sampleSize: number
  ): number[] {
    const speeds = [0];
    // Iterate through each point except the first one
    for (let i = 1; i < elapsedSeconds.length; i++) {
      const startIdx = Math.max(0, i - sampleSize + 1); // Start index of the rolling sample
      const distanceSumKm = (distances[i] - distances[startIdx]) / 1000; // Total distance covered within the sample
      const timeSumHours =
        (elapsedSeconds[i] - elapsedSeconds[startIdx]) / 3600; // Total time elapsed within the sample
      // Calculate speed (distance / time)
      const speed = Math.round((distanceSumKm / timeSumHours)*10)/10;

      // Add speed to the speeds array
      speeds.push(speed);
    }

    return speeds;
  }


}
